import { useState, useEffect } from 'react';

/**
 * Custom hook to manage collapsible section states with localStorage persistence
 * @param sectionKey - Unique key for the section (e.g., 'flowSummary', 'flowDetails')
 * @param defaultState - Default collapsed/expanded state
 * @returns [isExpanded, setIsExpanded] tuple
 */
export function useCollapsibleState(sectionKey: string, defaultState: boolean = true): [boolean, (state: boolean) => void] {
  const storageKey = `flowDetail_${sectionKey}_expanded`;
  
  // Initialize state from localStorage or use default
  const [isExpanded, setIsExpandedState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? JSON.parse(stored) : defaultState;
    } catch (error) {
      console.warn(`Failed to read ${storageKey} from localStorage:`, error);
      return defaultState;
    }
  });

  // Custom setter that also updates localStorage
  const setIsExpanded = (state: boolean) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
      setIsExpandedState(state);
    } catch (error) {
      console.warn(`Failed to save ${storageKey} to localStorage:`, error);
      setIsExpandedState(state);
    }
  };

  return [isExpanded, setIsExpanded];
}